import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { format } from "date-fns";
import React, { useRef } from "react";
import { Box } from "@material-ui/core";

ChartJS.register(
  TimeScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

/* distinct colours per series (colour-blind-safe) */
const COLORS = {
  tn_tp: "#377EB8",
  tdn_tdp: "#4DAF4A",
  tin_srp: "#FF7F00",
  chla: "#E41A1C",
  lmt: "#984EA3",
};

/* vertical guide-line */
const annotatedLine = {
  id: "guideLine",
  afterDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.();
    if (!active?.length) return;
    const x = active[0].element.x;
    const yScale = chart.scales.ratio;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = "rgba(0,0,150,0.4)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, chart.scales.chla.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

/* -------------------------------------------------------------------- */

export default function NutrientRatioChart({ series, height }) {
  const chartRef = useRef();

  const datasets = [
    {
      label: "TN:TP",
      data: series.tn_tp,
      yAxisID: "ratio",
      borderColor: COLORS.tn_tp,
      backgroundColor: COLORS.tn_tp,
      pointRadius: 3,
      spanGaps: false,
      borderDash: [], // solid
    },
    {
      label: "TDN:TDP",
      data: series.tdn_tdp,
      yAxisID: "ratio",
      borderColor: COLORS.tdn_tdp,
      backgroundColor: COLORS.tdn_tdp,
      pointRadius: 3,
      spanGaps: false,
      borderDash: [], // solid
    },
    {
      label: "TIN:SRP",
      data: series.tin_srp,
      yAxisID: "ratio",
      borderColor: COLORS.tin_srp,
      backgroundColor: COLORS.tin_srp,
      pointRadius: 3,
      spanGaps: false,
      borderDash: [], // solid
    },
    {
      label: "LMT",
      data: series.lmt,
      yAxisID: "ratio",
      borderColor: COLORS.lmt,
      backgroundColor: COLORS.lmt,
      pointRadius: 3,
      spanGaps: false,
      borderDash: [], // solid
    },
    {
      label: "Chl-a",
      data: series.chla,
      yAxisID: "chla",
      borderColor: COLORS.chla,
      backgroundColor: COLORS.chla,
      pointRadius: 3,
      spanGaps: false,
      borderDash: [6, 4], // dashed
    },
  ];

  const options = {
    maintainAspectRatio: false,
    interaction: { mode: "index", axis: "x", intersect: false },
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 15, weight: 600 } },
      },
      tooltip: {
        mode: "index",
        axis: "x",
        intersect: false,
        padding: 10,
        borderWidth: 1,
        borderColor: "#999",
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#333",
        cornerRadius: 6,
        titleFont: { size: 15, weight: 600 },
        bodyFont: { size: 15, weight: 600 },
        callbacks: {
          title: (items) => format(items[0].parsed.x, "MMM dd, yyyy"),
          label: (ti) => {
            const val = ti.parsed.y;
            if (val == null) return undefined;
            return `${ti.dataset.label}: ${val.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "month", displayFormats: { month: "MM/YY" } },
        ticks: { font: { size: 15, weight: 600 } },
      },
      ratio: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: "Nitrogen to Phosphorus Ratio",
          font: { size: 15, weight: 600 },
        },
        ticks: { font: { size: 15, weight: 600 } },
      },
      chla: {
        type: "linear",
        position: "right",
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: "Chlorophyll-a (µg L⁻¹)",
          font: { size: 15, weight: 600 },
        },
        ticks: { font: { size: 15, weight: 600 } },
      },
    },
  };

  return (
    <Box height={height} width="100%">
      <Line
        ref={chartRef}
        height={height}
        data={{ datasets }}
        options={options}
        plugins={[annotatedLine]}
      />
    </Box>
  );
}
